import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ProveedorAliasChips from "@/components/ProveedorAliasChips";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Plus, Mail, Pencil, Ship, FileCheck, FileX, FileText,
  Loader2, Upload, Trash2, ExternalLink, CalendarIcon,
} from "lucide-react";
import {
  usePartnerStats,
  usePartnerOperations,
  usePartnerActivity,
  usePartnerDocuments,
  useUploadPartnerDocument,
  useDeletePartnerDocument,
  useUpdatePartnerDocument,
} from "@/hooks/usePartnerDetails";
import PartnerFormModal from "@/components/PartnerFormModal";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { DocumentViewerModal } from "@/components/DocumentViewerModal";

export interface PartnerData {
  id: string;
  nombre: string;
  nit?: string | null;
  contacto?: string | null;
  email?: string | null;
  telefono?: string | null;
  direccion?: string | null;
  ciudad?: string | null;
  pais?: string | null;
  notas?: string | null;
  created_at: string;
}

interface PartnerDetailSheetProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  partner: PartnerData | null;
  type: "proveedor" | "cliente";
}

function getInitials(name: string) {
  return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function StatCard({ value, label, warn }: { value: string; label: string; warn?: boolean }) {
  return (
    <Card className="shadow-none border-border">
      <CardContent className="p-3 text-center space-y-0.5">
        <p className={`text-2xl font-bold ${warn ? "text-amber-600 dark:text-amber-400" : "text-foreground"}`}>{value}</p>
        <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
      </CardContent>
    </Card>
  );
}

function EmptyTab({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
      <FileText className="h-8 w-8 opacity-30" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

// ── Upload Document Modal ──
function UploadDocModal({
  open,
  onOpenChange,
  partnerId,
  partnerType,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  partnerId: string;
  partnerType: "proveedor" | "cliente";
}) {
  const [nombre, setNombre] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fecha, setFecha] = useState<Date>();
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadPartnerDocument();

  const handleSubmit = async () => {
    if (!file || !nombre.trim()) {
      toast.error("Selecciona un archivo y escribe un nombre.");
      return;
    }
    try {
      await upload.mutateAsync({
        socioId: partnerId,
        tipoSocio: partnerType,
        file,
        nombreArchivo: nombre.trim(),
        fechaVencimiento: fecha ? format(fecha, "yyyy-MM-dd") : undefined,
      });
      toast.success("Documento subido correctamente");
      setNombre("");
      setFile(null);
      setFecha(undefined);
      onOpenChange(false);
    } catch {
      toast.error("Error al subir el documento");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Subir Documento del Socio</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Nombre del Documento *</Label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: RUT, Cámara de Comercio..." />
          </div>
          <div className="space-y-2">
            <Label>Archivo (PDF / Imagen) *</Label>
            <Input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className="space-y-2">
            <Label>Fecha de Vencimiento (Opcional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !fecha && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {fecha ? format(fecha, "PPP", { locale: es }) : "Seleccionar fecha"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={fecha} onSelect={setFecha} initialFocus className="p-3 pointer-events-auto" />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={upload.isPending}>
            {upload.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Subiendo...</> : "Subir Documento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Document Modal ──
function EditDocModal({
  open,
  onOpenChange,
  document,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  document: any;
}) {
  const [nombre, setNombre] = useState("");
  const [fecha, setFecha] = useState<Date>();
  const updateDoc = useUpdatePartnerDocument();

  // Pre-fill when document changes
  useState(() => {
    if (document) {
      setNombre(document.nombre_archivo || "");
      setFecha(document.fecha_vencimiento ? new Date(document.fecha_vencimiento) : undefined);
    }
  });

  // Sync when document changes
  const [lastDocId, setLastDocId] = useState<string | null>(null);
  if (document && document.id !== lastDocId) {
    setLastDocId(document.id);
    setNombre(document.nombre_archivo || "");
    setFecha(document.fecha_vencimiento ? new Date(document.fecha_vencimiento) : undefined);
  }

  const handleSave = async () => {
    if (!nombre.trim()) {
      toast.error("El nombre del documento es requerido.");
      return;
    }
    try {
      await updateDoc.mutateAsync({
        id: document.id,
        nombreArchivo: nombre.trim(),
        fechaVencimiento: fecha ? format(fecha, "yyyy-MM-dd") : null,
      });
      toast.success("Documento actualizado correctamente");
      onOpenChange(false);
    } catch {
      toast.error("Error al actualizar el documento");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Documento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Nombre del Documento *</Label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: RUT, Cámara de Comercio..." />
          </div>
          <div className="space-y-2">
            <Label>Fecha de Vencimiento (Opcional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !fecha && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {fecha ? format(fecha, "PPP", { locale: es }) : "Seleccionar fecha"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={fecha} onSelect={setFecha} initialFocus className="p-3 pointer-events-auto" />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={updateDoc.isPending}>
            {updateDoc.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando...</> : "Guardar Cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PartnerDetailSheet({ open, onOpenChange, partner, type }: PartnerDetailSheetProps) {
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewerDoc, setViewerDoc] = useState<{ url: string; name: string } | null>(null);
  const [editingDoc, setEditingDoc] = useState<any>(null);

  const partnerName = partner?.nombre ?? "";
  const { data: stats, isLoading: loadingStats } = usePartnerStats(partner?.id ?? null, partnerName, type);
  const { data: operations, isLoading: loadingOps } = usePartnerOperations(partner?.id ?? null, partnerName, type);
  const { data: activity, isLoading: loadingActivity } = usePartnerActivity(partner?.id ?? null, partnerName, type);
  const { data: documents, isLoading: loadingDocs } = usePartnerDocuments(partner?.id ?? null, type);
  const deleteDoc = useDeletePartnerDocument();

  if (!partner) return null;

  const isExpired = (d: string | null) => d ? new Date(d) < new Date() : false;

  const handleNewOp = () => {
    onOpenChange(false);
    navigate(`/dashboard/embarques?nuevo=true&proveedorId=${partner.id}`);
  };

  const handleMail = () => {
    if (partner.email) window.location.href = `mailto:${partner.email}`;
  };

  const handleViewDoc = async (archivoUrl: string, nombre: string) => {
    setViewerDoc({ url: archivoUrl, name: nombre });
  };

  const handleDeleteDoc = async (id: string, archivoUrl: string) => {
    try {
      await deleteDoc.mutateAsync({ id, archivoUrl });
      toast.success("Documento eliminado");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const mailButton = (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5 text-xs h-8"
      onClick={handleMail}
      disabled={!partner.email}
    >
      <Mail className="h-3.5 w-3.5" /> Correo
    </Button>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-lg w-full p-0 flex flex-col gap-0">
          {/* ── Header ── */}
          <SheetHeader className="p-6 pb-4 border-b border-border space-y-4">
            <div className="flex items-start gap-4">
              <Avatar className="h-14 w-14 text-lg border-2 border-primary/20">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {getInitials(partner.nombre)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 space-y-1">
                <SheetTitle className="text-base font-semibold truncate">{partner.nombre}</SheetTitle>
                <p className="text-xs text-muted-foreground font-mono">{partner.nit || "Sin NIT"}</p>
                <div className="flex items-center gap-2 pt-0.5">
                  <Badge className="bg-primary/15 text-primary border-primary/20 hover:bg-primary/15 text-[10px]">
                    Socio Verificado
                  </Badge>
                  {partner.pais && <span className="text-[11px] text-muted-foreground">{partner.pais}</span>}
                </div>
              </div>
              {type === "proveedor" && <ProveedorAliasChips proveedorId={partner.id} />}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={handleNewOp}>
                <Plus className="h-3.5 w-3.5" /> Nueva Operación
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-block">
                      {mailButton}
                    </span>
                  </TooltipTrigger>
                  {!partner.email && (
                    <TooltipContent>
                      <p className="text-xs">Añade un email editando el perfil para habilitar esta opción</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={() => setEditOpen(true)}>
                <Pencil className="h-3.5 w-3.5" /> Editar
              </Button>
            </div>
          </SheetHeader>

          {/* ── Quick Stats ── */}
          <div className="grid grid-cols-3 gap-3 px-6 py-4 border-b border-border">
            {loadingStats ? (
              <>{[1, 2, 3].map((i) => <Skeleton key={i} className="h-[72px] rounded-lg" />)}</>
            ) : (
              <>
                <StatCard value={String(stats?.opsCount ?? 0)} label="Operaciones (Año)" />
                <StatCard value={stats?.avgDays != null ? `${stats.avgDays} días` : "N/A"} label="Tiempo Promedio" />
                <StatCard value={String(stats?.pendingDocs ?? 0)} label="Docs Pendientes" warn={(stats?.pendingDocs ?? 0) > 0} />
              </>
            )}
          </div>

          {/* ── Tabs ── */}
          <div className="flex-1 overflow-y-auto">
            <Tabs defaultValue="activity" className="h-full flex flex-col">
              <TabsList className="mx-6 mt-4 mb-2 w-fit bg-muted/60">
                <TabsTrigger value="activity" className="text-xs">Actividad</TabsTrigger>
                <TabsTrigger value="ops" className="text-xs">Operaciones</TabsTrigger>
                <TabsTrigger value="docs" className="text-xs">Documentación</TabsTrigger>
              </TabsList>

              {/* Activity Feed */}
              <TabsContent value="activity" className="px-6 pb-6 flex-1">
                {loadingActivity ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : !activity || activity.length === 0 ? (
                  <EmptyTab text="Sin actividad registrada aún" />
                ) : (
                  <div className="relative pl-6 space-y-6 pt-2">
                    <div className="absolute left-[9px] top-4 bottom-2 w-px bg-border" />
                    {activity.map((doc) => (
                      <div key={doc.id} className="relative flex gap-3 items-start">
                        <div className="absolute -left-6 top-0.5 h-[18px] w-[18px] rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center">
                          <FileText className="h-2.5 w-2.5 text-primary" />
                        </div>
                        <div className="space-y-0.5">
                          <p className="text-sm text-foreground leading-snug">
                            {doc.tipo_documento || "Documento"}{" "}
                            <span className="font-mono text-xs text-muted-foreground">#{doc.no_documento}</span>
                            {" — "}
                            <Badge variant="outline" className="text-[10px] ml-1">{doc.estado}</Badge>
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {formatDistanceToNow(new Date(doc.created_at), { addSuffix: true, locale: es })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Operaciones */}
              <TabsContent value="ops" className="px-6 pb-6 flex-1">
                {loadingOps ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : !operations || operations.length === 0 ? (
                  <EmptyTab text="Sin operaciones registradas" />
                ) : (
                  <div className="space-y-2 pt-1">
                    {operations.map((op) => (
                      <div key={op.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-card hover:bg-muted/40 transition-colors">
                        <div className="space-y-0.5">
                          <p className="text-sm font-medium text-foreground font-mono">{op.numero_do}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {op.proveedor || "—"} · {op.fecha_llegada_estimada
                              ? new Date(op.fecha_llegada_estimada).toLocaleDateString("es-CO", { day: "2-digit", month: "short" })
                              : "Sin ETA"}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={
                            op.estado_logistico === "Entregado"
                              ? "border-emerald-500/30 text-emerald-700 dark:text-emerald-400 text-[10px]"
                              : op.estado_logistico === "En Aduana"
                                ? "border-amber-500/30 text-amber-700 dark:text-amber-400 text-[10px]"
                                : "border-primary/30 text-primary text-[10px]"
                          }
                        >
                          <Ship className="h-3 w-3 mr-1" />
                          {op.estado_logistico}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Documentación (Master documents) */}
              <TabsContent value="docs" className="px-6 pb-6 flex-1">
                <div className="flex justify-end mb-3">
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => setUploadOpen(true)}>
                    <Upload className="h-3.5 w-3.5" /> Subir Documento
                  </Button>
                </div>
                {loadingDocs ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : !documents || documents.length === 0 ? (
                  <EmptyTab text="Sin documentos del socio" />
                ) : (
                  <div className="space-y-2 pt-1">
                    {documents.map((doc: any) => {
                      const expired = isExpired(doc.fecha_vencimiento);
                      const hasExpiry = !!doc.fecha_vencimiento;
                      return (
                        <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-card">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {expired ? <FileX className="h-4 w-4 shrink-0 text-destructive" /> : <FileCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />}
                            <div className="min-w-0">
                              <p className="text-sm text-foreground truncate">{doc.nombre_archivo}</p>
                              {hasExpiry && (
                                <p className="text-[10px] text-muted-foreground">
                                  Vence: {new Date(doc.fecha_vencimiento).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" })}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge
                              variant="outline"
                              className={
                                expired
                                  ? "border-destructive/30 text-destructive text-[10px]"
                                  : hasExpiry
                                    ? "border-emerald-500/30 text-emerald-700 dark:text-emerald-400 text-[10px]"
                                    : "border-border text-muted-foreground text-[10px]"
                              }
                            >
                              {expired ? "Expirado" : hasExpiry ? "Vigente" : "Sin fecha"}
                            </Badge>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleViewDoc(doc.archivo_url, doc.nombre_archivo)}>
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setEditingDoc(doc)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteDoc(doc.id, doc.archivo_url)}
                              disabled={deleteDoc.isPending}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>

      <PartnerFormModal open={editOpen} onOpenChange={setEditOpen} partner={partner} type={type} />
      <UploadDocModal open={uploadOpen} onOpenChange={setUploadOpen} partnerId={partner.id} partnerType={type} />
      <EditDocModal open={!!editingDoc} onOpenChange={(v) => { if (!v) setEditingDoc(null); }} document={editingDoc} />
      <DocumentViewerModal
        open={!!viewerDoc}
        onOpenChange={(v) => { if (!v) setViewerDoc(null); }}
        url={viewerDoc?.url ?? ""}
        title={viewerDoc?.name}
        bucket="documentos_partners"
      />
    </>
  );
}
